import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/base/SectionHeading";
import VideoCard from "@/components/base/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { copyToClipboard, formatCount } from "@/utils/format";
import { getRelatedVideos, getVideoById } from "@/utils/videoData";

const shareTargets = [
  { icon: "ri-wechat-fill", label: "微信" },
  { icon: "ri-weibo-fill", label: "微博" },
  { icon: "ri-qq-fill", label: "QQ" },
  { icon: "ri-link", label: "复制链接" },
];

export default function DetailPage() {
  const { videoId } = useParams();
  const video = getVideoById(videoId);
  const [playing, setPlaying] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setPlaying(false);
    setToast("");
  }, [videoId]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(timer);
  }, [toast]);

  usePageMeta({
    title: video ? `${video.title} - 高清在线观看 - 一起草视频` : "视频详情 - 一起草视频",
    description: video
      ? `观看“${video.title}”，这是一起草视频提供的高质量影视娱乐内容。`
      : "一起草视频视频详情页。",
    keywords: video ? `一起草视频,${video.title},${video.genre},高清在线观看` : "一起草视频,视频详情",
  });

  if (!video) {
    return (
      <div className="px-4 md:px-6 py-16 max-w-3xl mx-auto">
        <EmptyState
          icon="ri-movie-2-line"
          title="视频不存在或已下架"
          description="你访问的视频可能已被移除，返回首页看看其他精彩内容吧。"
          actionText="返回首页"
          actionLink="/"
        />
      </div>
    );
  }

  const related = getRelatedVideos(video, 6);

  const handleShare = (label: string) => {
    if (label === "复制链接") {
      copyToClipboard(window.location.href).then(() => setToast("链接已复制到剪贴板"));
    } else {
      setToast(`已开启${label}分享（演示）`);
    }
  };

  return (
    <div className="bg-background-50">
      <div className="px-4 md:px-6 pt-6 md:pt-8">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-5 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            首页
          </Link>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-base" />
          </span>
          <Link to={`/category/${video.categoryId}`} className="hover:text-primary-600 transition-colors">
            {video.categoryName}
          </Link>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-base" />
          </span>
          <span className="text-foreground-900 clamp-1">{video.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="min-w-0 flex-1">
            <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-foreground-950">
              <img
                src={video.cover}
                alt={video.title}
                title={`${video.title} - 高清在线观看`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-foreground-950/35" />
              {!playing ? (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  aria-label="播放视频"
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 cursor-pointer group"
                >
                  <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-accent-500 text-primary-950 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-3xl md:text-4xl" />
                  </span>
                  <span className="text-background-50 text-sm md:text-base font-medium">点击播放（演示）</span>
                </button>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-foreground-950/85 text-background-50 animate-fade-in">
                  <span className="w-16 h-16 rounded-full border-2 border-dashed border-accent-400 flex items-center justify-center mb-4 animate-spin" style={{ animationDuration: "3s" }}>
                    <i className="ri-loader-4-line text-2xl text-accent-400" />
                  </span>
                  <p className="text-base md:text-lg font-semibold">视频播放器演示</p>
                  <p className="mt-2 text-xs md:text-sm text-background-100/70 max-w-md text-center px-6">
                    当前为前端模拟播放区域，不提供真实视频播放与下载功能。
                  </p>
                  <button
                    type="button"
                    onClick={() => setPlaying(false)}
                    className="mt-5 h-10 px-5 rounded-md border border-background-50/40 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    关闭播放器
                  </button>
                </div>
              )}
            </div>

            <h1 className="mt-5 text-xl md:text-3xl font-extrabold text-foreground-950">{video.title}</h1>
            <p className="mt-2 text-sm md:text-base text-foreground-500">{video.subtitle}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-star-line text-base" />
                </span>
                {video.uploader}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-base" />
                </span>
                {video.uploadTime}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-base" />
                </span>
                {video.duration}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line text-base" />
                </span>
                {formatCount(video.plays)} 次播放
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bookmark-line text-base" />
                </span>
                {formatCount(video.favorites)} 收藏
              </span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <span className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium">{video.categoryName}</span>
              <span className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium">{video.genre}</span>
              <span className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium">{video.region}</span>
              <span className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium">{video.year}</span>
              {video.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-md bg-background-100 text-foreground-600 text-xs hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 p-5 rounded-lg bg-background-100 border border-background-200">
              <h2 className="text-base font-bold text-foreground-950 mb-3">视频简介</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{video.description}</p>
            </div>

            <div className="mt-6 p-5 rounded-lg bg-background-100 border border-background-200">
              <h2 className="text-base font-bold text-foreground-950 mb-3">分享这个视频</h2>
              <div className="flex flex-wrap items-center gap-3">
                {shareTargets.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => handleShare(t.label)}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${t.icon} text-base`} />
                    </span>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Link
                to={`/category/${video.categoryId}`}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-base" />
                </span>
                返回{video.categoryName}
              </Link>
            </div>
          </div>

          <aside className="w-full lg:w-80 shrink-0">
            <div className="p-5 rounded-lg bg-primary-950 text-background-50">
              <h2 className="text-base font-bold mb-4">内容信息</h2>
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-primary-200/80">分类</dt>
                  <dd className="font-medium">{video.categoryName}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-primary-200/80">类型</dt>
                  <dd className="font-medium">{video.genre}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-primary-200/80">地区</dt>
                  <dd className="font-medium">{video.region}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-primary-200/80">年份</dt>
                  <dd className="font-medium">{video.year}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-primary-200/80">时长</dt>
                  <dd className="font-medium">{video.duration}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-primary-200/80">上传时间</dt>
                  <dd className="font-medium">{video.uploadTime}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-primary-200/80">上传者</dt>
                  <dd className="font-medium">{video.uploader}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>

        <section className="mt-10 md:mt-14 pb-4">
          <SectionHeading icon="ri-movie-line" title="相关推荐" subtitle="根据分类与标签为你匹配" />
          {related.length === 0 ? (
            <EmptyState icon="ri-film-line" title="暂无相关推荐" description="看看其他分类的精彩内容吧。" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-5">
              {related.map((item, idx) => (
                <VideoCard key={item.id} video={item} index={idx} />
              ))}
            </div>
          )}
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}